import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Dna, Flame, Brain, Utensils, Users, Trophy, 
  Camera, Target, ArrowRight, Sparkles, Dumbbell
} from 'lucide-react';

/**
 * Ecosystem Map 3D v3 - CIRCULAR with PULSING LIGHT BEAMS
 * - Perfect circular orbit (not elliptical)
 * - Central energy sphere
 * - Animated light beams from center to each module
 * - All modules always visible
 */
const EcosystemMap = ({ onModuleClick }) => {
  const { t } = useTranslation();
  const [hoveredModule, setHoveredModule] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [pulsePhase, setPulsePhase] = useState(0);
  const canvasRef = useRef(null);
  
  // Smooth circular rotation
  useEffect(() => {
    let lastTime = performance.now();
    let animationId;
    
    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;
      
      // Rotation speed
      const speed = 1.2 * (deltaTime / 16.67);
      setRotation(prev => (prev + speed) % 360);
      setPulsePhase(prev => (prev + 0.05) % (Math.PI * 2));
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);
  
  // Canvas for central energy and light beams
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.03;
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const orbitRadius = 130;
      
      // Draw pulsing light beams to each module
      for (let i = 0; i < 10; i++) {
        const angle = (i * 36 + rotation) * (Math.PI / 180);
        const endX = centerX + Math.cos(angle) * orbitRadius;
        const endY = centerY + Math.sin(angle) * orbitRadius;
        
        // Animated pulse along the beam
        const pulsePos = ((time * 2 + i * 0.5) % 1);
        const pulseX = centerX + (endX - centerX) * pulsePos;
        const pulseY = centerY + (endY - centerY) * pulsePos;
        
        // Main beam (faint)
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(endX, endY);
        ctx.strokeStyle = 'rgba(163, 230, 53, 0.15)';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Energy pulse traveling along beam
        const pulseGradient = ctx.createRadialGradient(pulseX, pulseY, 0, pulseX, pulseY, 15);
        pulseGradient.addColorStop(0, 'rgba(163, 230, 53, 0.8)');
        pulseGradient.addColorStop(0.5, 'rgba(163, 230, 53, 0.3)');
        pulseGradient.addColorStop(1, 'rgba(163, 230, 53, 0)');
        
        ctx.beginPath();
        ctx.arc(pulseX, pulseY, 15, 0, Math.PI * 2);
        ctx.fillStyle = pulseGradient;
        ctx.fill();
        
        // Glowing line segment (the pulse itself)
        const segmentStart = Math.max(0, pulsePos - 0.15);
        const segmentEnd = pulsePos;
        
        ctx.beginPath();
        ctx.moveTo(
          centerX + (endX - centerX) * segmentStart,
          centerY + (endY - centerY) * segmentStart
        );
        ctx.lineTo(
          centerX + (endX - centerX) * segmentEnd,
          centerY + (endY - centerY) * segmentEnd
        );
        ctx.strokeStyle = `rgba(163, 230, 53, ${0.6 * (1 - pulsePos)})`;
        ctx.lineWidth = 3;
        ctx.shadowBlur = 10;
        ctx.shadowColor = '#a3e635';
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
      
      // Outer ambient glow
      const ambientGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 160);
      ambientGlow.addColorStop(0, 'rgba(163, 230, 53, 0.15)');
      ambientGlow.addColorStop(0.5, 'rgba(163, 230, 53, 0.05)');
      ambientGlow.addColorStop(1, 'rgba(163, 230, 53, 0)');
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, 160, 0, Math.PI * 2);
      ctx.fillStyle = ambientGlow;
      ctx.fill();
      
      // Central energy sphere glow
      const coreIntensity = 0.6 + Math.sin(time * 3) * 0.2;
      
      // Outer glow
      const outerGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 50);
      outerGlow.addColorStop(0, `rgba(163, 230, 53, ${coreIntensity * 0.5})`);
      outerGlow.addColorStop(0.6, `rgba(163, 230, 53, ${coreIntensity * 0.2})`);
      outerGlow.addColorStop(1, 'rgba(163, 230, 53, 0)');
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, 50, 0, Math.PI * 2);
      ctx.fillStyle = outerGlow;
      ctx.fill();
      
      // Core sphere
      const coreGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 25);
      coreGlow.addColorStop(0, `rgba(217, 249, 157, ${coreIntensity})`);
      coreGlow.addColorStop(0.5, `rgba(163, 230, 53, ${coreIntensity * 0.8})`);
      coreGlow.addColorStop(1, `rgba(101, 163, 13, ${coreIntensity * 0.5})`);
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, 25, 0, Math.PI * 2);
      ctx.fillStyle = coreGlow;
      ctx.shadowBlur = 30;
      ctx.shadowColor = '#a3e635';
      ctx.fill();
      ctx.shadowBlur = 0;
      
      // Inner bright core
      ctx.beginPath();
      ctx.arc(centerX, centerY, 8, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${0.8 + Math.sin(time * 5) * 0.2})`;
      ctx.fill();
      
      animationId = requestAnimationFrame(animate);
    };
    
    canvas.width = 400;
    canvas.height = 400;
    animate();
    
    return () => cancelAnimationFrame(animationId);
  }, [rotation]);
  
  const modules = [
    { id: 'body-twin', name: t('landing.ecosystem.modules.bodyTwin.name'), description: t('landing.ecosystem.modules.bodyTwin.desc'), icon: Dna, color: '#a3e635', angle: 0 },
    { id: 'fasting', name: t('landing.ecosystem.modules.fasting.name'), description: t('landing.ecosystem.modules.fasting.desc'), icon: Flame, color: '#f97316', angle: 36 },
    { id: 'neuroketo', name: t('landing.ecosystem.modules.onspirit.name') || 'NeuroKeto', description: t('landing.ecosystem.modules.onspirit.desc') || 'Coach IA de mindset e comportamento', icon: Sparkles, color: '#8b5cf6', angle: 72 },
    { id: 'ai-coach', name: t('landing.ecosystem.modules.aiCoach.name'), description: t('landing.ecosystem.modules.aiCoach.desc'), icon: Brain, color: '#a855f7', angle: 108 },
    { id: 'meals', name: t('landing.ecosystem.modules.aiChef.name'), description: t('landing.ecosystem.modules.aiChef.desc'), icon: Utensils, color: '#ec4899', angle: 144 },
    { id: 'community', name: t('landing.ecosystem.modules.community.name'), description: t('landing.ecosystem.modules.community.desc'), icon: Users, color: '#06b6d4', angle: 180 },
    { id: 'challenge', name: t('landing.ecosystem.modules.challenge.name'), description: t('landing.ecosystem.modules.challenge.desc'), icon: Trophy, color: '#eab308', angle: 216 },
    { id: 'training', name: t('landing.ecosystem.modules.metabolicWorkout.name') || 'Metabolic Workout', description: t('landing.ecosystem.modules.metabolicWorkout.desc') || 'Treinos HIIT + Mind & Body', icon: Dumbbell, color: '#ef4444', angle: 252 },
    { id: 'progress', name: t('landing.ecosystem.modules.analytics.name'), description: t('landing.ecosystem.modules.analytics.desc'), icon: Target, color: '#22c55e', angle: 288 },
    { id: 'ketoscan', name: t('landing.ecosystem.modules.ketoScan.name'), description: t('landing.ecosystem.modules.ketoScan.desc'), icon: Camera, color: '#14b8a6', angle: 324 },
  ];
  
  // Calculate CIRCULAR position (not elliptical)
  const getPosition = (module, orbitRadius = 130) => {
    const angle = (module.angle + rotation) * (Math.PI / 180);
    
    // PERFECT CIRCLE - same radius for X and Y
    const x = Math.cos(angle) * orbitRadius;
    const y = Math.sin(angle) * orbitRadius;
    
    return { x, y, angle };
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto py-8">
      {/* Title - World Class */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#a3e635]/10 border border-[#a3e635]/30 mb-4">
          <Sparkles className="w-4 h-4 text-[#a3e635] animate-pulse" />
          <span className="text-[#a3e635] text-xs font-medium tracking-wider uppercase">World Class Platform</span>
        </div>
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
          {t('landing.ecosystem.title')}
        </h3>
        <p className="text-gray-400 text-xs sm:text-sm max-w-xl mx-auto">
          {t('landing.ecosystem.subtitle')}
        </p>
      </div>
      
      {/* Circular Orbit Container */}
      <div className="relative h-[400px] sm:h-[450px] md:h-[500px] flex items-center justify-center">
        
        {/* Orbit ring (PERFECT CIRCLE) */}
        <div className="absolute w-[260px] h-[260px] sm:w-[300px] sm:h-[300px] md:w-[340px] md:h-[340px] 
                      border border-[#a3e635]/20 rounded-full"
             style={{ 
               boxShadow: '0 0 30px rgba(163, 230, 53, 0.1) inset, 0 0 60px rgba(163, 230, 53, 0.05)'
             }} />
        
        {/* Canvas for energy sphere and light beams */}
        <canvas 
          ref={canvasRef}
          className="absolute pointer-events-none"
          style={{ width: '400px', height: '400px', zIndex: 5 }}
        />
        
        {/* Central brand text */}
        <div className="absolute z-20 text-center" style={{ marginTop: '80px' }}>
          <p className="text-sm sm:text-base font-bold text-[#a3e635] drop-shadow-[0_0_15px_rgba(163,230,53,0.8)]">
            Keto Lab
          </p>
        </div>
        
        {/* Orbiting Modules - CIRCULAR */}
        {modules.map((mod) => {
          const Icon = mod.icon;
          const isHovered = hoveredModule === mod.id;
          const pos = getPosition(mod, 130);
          
          return (
            <div
              key={mod.id}
              className="absolute transition-all duration-200 cursor-pointer z-10"
              style={{
                left: `calc(50% + ${pos.x}px)`,
                top: `calc(50% + ${pos.y}px)`,
                transform: `translate(-50%, -50%) scale(${isHovered ? 1.15 : 1})`,
                zIndex: isHovered ? 50 : 10,
              }}
              onMouseEnter={() => setHoveredModule(mod.id)}
              onMouseLeave={() => setHoveredModule(null)}
              onClick={() => onModuleClick?.(mod.id)}
            >
              {/* Module icon */}
              <div 
                className="relative rounded-xl flex items-center justify-center
                           border-2 transition-all duration-200"
                style={{ 
                  width: isHovered ? '58px' : '50px',
                  height: isHovered ? '58px' : '50px',
                  borderColor: mod.color,
                  backgroundColor: '#0a0a0a',
                  boxShadow: isHovered 
                    ? `0 0 30px ${mod.color}80, 0 0 50px ${mod.color}40, inset 0 0 15px ${mod.color}20` 
                    : `0 0 20px ${mod.color}50, inset 0 0 10px ${mod.color}10`,
                }}
              >
                <Icon 
                  style={{ 
                    color: mod.color,
                    width: isHovered ? '26px' : '22px',
                    height: isHovered ? '26px' : '22px',
                    filter: `drop-shadow(0 0 8px ${mod.color})`,
                  }}
                />
              </div>
              
              {/* Label */}
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-center">
                <p 
                  className="text-[9px] sm:text-[11px] font-bold px-2 py-0.5 rounded"
                  style={{ 
                    color: isHovered ? mod.color : '#fff',
                    backgroundColor: isHovered ? 'rgba(0,0,0,0.9)' : 'transparent',
                    textShadow: `0 0 10px ${mod.color}80`,
                  }}
                >
                  {mod.name}
                </p>
              </div>
              
              {/* Tooltip on hover */}
              {isHovered && (
                <div 
                  className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-44 p-3
                           bg-black/95 backdrop-blur-xl rounded-xl shadow-2xl z-50
                           border-2"
                  style={{ 
                    borderColor: `${mod.color}60`,
                    boxShadow: `0 0 25px ${mod.color}40`
                  }}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon className="w-4 h-4" style={{ color: mod.color }} />
                    <p className="text-sm font-bold text-white">{mod.name}</p>
                  </div>
                  <p className="text-[11px] text-gray-300 leading-relaxed">{mod.description}</p>
                  <div className="flex items-center gap-1 mt-2 text-[11px] font-medium" style={{ color: mod.color }}>
                    <span>{t('landing.ecosystem.explore')}</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-4 px-4">
        {modules.map(mod => (
          <div 
            key={mod.id} 
            className="flex items-center gap-2 text-[9px] sm:text-[11px] cursor-pointer 
                     hover:opacity-100 opacity-80 transition-all"
            onClick={() => onModuleClick?.(mod.id)}
          >
            <div 
              className="w-2.5 h-2.5 rounded-full" 
              style={{ backgroundColor: mod.color, boxShadow: `0 0 8px ${mod.color}80` }} 
            />
            <span className="text-gray-300 font-medium">{mod.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EcosystemMap;
